import { Add, MedicalServices, MedicationOutlined, ReceiptLong } from "@mui/icons-material";
import {
  Box,
  Button,
  Stack,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useEffect, useState, type ReactNode } from "react";

import { CircularLoading } from "@/components/common";
import { MobileBottomSheet } from "@/components/common/MobileBottomSheet";
import { PageHeader } from "@/components/common/PageHeader";
import { ScreenMobile } from "@/components/common/ScreenMobile";
import { useDocumentDialog } from "@/hooks/useDocumentDialog";
import { MedicalNotes } from "@/pages/ehrrecords/components/patientSummaryAccordions/Documents/components/MedicalNotes";
import { IframeDialog } from "@/pages/ehrrecords/components/IframeDialog";

import { NewMedication } from "../../NewMedication";
import { useMedicationControl } from "../../../../../hooks/useMedicationControl";
import { ControlPrescriptions } from "../../ControlPrescriptions";

type MedicationsTab = "prescriptions" | "manage";

const NEW_MEDICATION_FORM_ID = "control-medications-new-form";

interface ControlMedicationsProps {
  open?: boolean;
  setOpen?: (open: boolean) => void;
  /** Conteúdo sem shell — o pai renderiza `ScreenMobile` (ex.: PatientDash mobile). */
  embedded?: boolean;
}

function MedicationsSegmentedTab({
  label,
  icon,
  count,
  selected,
  onClick,
}: {
  label: string;
  icon: ReactNode;
  count: number;
  selected: boolean;
  onClick: () => void;
}) {
  const theme = useTheme();

  return (
    <Box
      component="button"
      onClick={onClick}
      aria-selected={selected}
      sx={{
        flex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 0.75,
        py: 0.85,
        px: 1,
        border: "none",
        borderRadius: 99,
        cursor: "pointer",
        bgcolor: selected ? "background.paper" : "transparent",
        color: selected ? "primary.main" : "text.secondary",
        boxShadow: selected ? "0 1px 4px rgba(15, 23, 42, 0.08)" : "none",
        transition: "all 0.18s ease",
        outline: "none",
        minWidth: 0,
        "& svg": { fontSize: "1.05rem", flexShrink: 0 },
      }}
    >
      {icon}
      <Typography
        component="span"
        sx={{
          fontWeight: selected ? 700 : 500,
          fontSize: "0.8125rem",
          lineHeight: 1.2,
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </Typography>
      <Box
        component="span"
        sx={{
          minWidth: 20,
          height: 20,
          px: 0.5,
          borderRadius: 99,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "0.68rem",
          fontWeight: 700,
          bgcolor: selected
            ? alpha(theme.palette.primary.main, 0.12)
            : alpha(theme.palette.grey[500], 0.15),
          color: selected ? "primary.main" : "text.disabled",
        }}
      >
        {count}
      </Box>
    </Box>
  );
}

function MedicationsTabsBar({
  selectedTab,
  onSelectTab,
  manageCount,
  prescriptionCount,
}: {
  selectedTab: MedicationsTab;
  onSelectTab: (tab: MedicationsTab) => void;
  manageCount: number;
  prescriptionCount: number;
}) {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: "flex",
        gap: 0.5,
        p: 0.5,
        borderRadius: 99,
        bgcolor: alpha(theme.palette.grey[500], 0.1),
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <MedicationsSegmentedTab
        label="Gerenciar"
        icon={<MedicationOutlined />}
        count={manageCount}
        selected={selectedTab === "manage"}
        onClick={() => onSelectTab("manage")}
      />
      <MedicationsSegmentedTab
        label="Receitas"
        icon={<ReceiptLong />}
        count={prescriptionCount}
        selected={selectedTab === "prescriptions"}
        onClick={() => onSelectTab("prescriptions")}
      />
    </Box>
  );
}

/** Conteúdo interno — abas Gerenciar / Receitas (mobile). */
function ControlMedicationsMobileBody({
  selectedTab,
  onSelectTab,
  manageCount,
  prescriptionCount,
  manageTabContent,
  prescriptionsContent,
}: {
  selectedTab: MedicationsTab;
  onSelectTab: (tab: MedicationsTab) => void;
  manageCount: number;
  prescriptionCount: number;
  manageTabContent: ReactNode;
  prescriptionsContent: ReactNode;
}) {
  return (
    <Stack width="100%" spacing={1.5} sx={{ pt: 0.5, minWidth: 0 }}>
      <MedicationsTabsBar
        selectedTab={selectedTab}
        onSelectTab={onSelectTab}
        manageCount={manageCount}
        prescriptionCount={prescriptionCount}
      />
      {selectedTab === "prescriptions" ? prescriptionsContent : manageTabContent}
    </Stack>
  );
}

export function ControlMedications({
  open,
  setOpen,
  embedded = false,
}: ControlMedicationsProps) {
  const theme = useTheme();
  const isMobileLayout = useMediaQuery(theme.breakpoints.down("md"));
  const [openNewMedication, setOpenNewMedication] = useState(false);
  const [selectedTab, setSelectedTab] = useState<MedicationsTab>("manage");

  const {
    open: iframeOpen,
    binaryUrl,
    setBinaryUrl,
    handleOpen,
    handleClose,
  } = useDocumentDialog();

  const {
    isInitialLoading,
    isRefetching,
    medications: medicationData,
    medicationRequestEntries,
    prescriptionDocuments,
    prescriptionRowData,
    prescriptionCount,
    refetch: setRefetchMedication,
  } = useMedicationControl();

  const manageCount = medicationData?.length ?? 0;

  useEffect(() => {
    if (open) {
      setSelectedTab("manage");
      setOpenNewMedication(false);
    }
  }, [open]);

  const handleOpenAddForm = () => {
    setSelectedTab("manage");
    setOpenNewMedication(true);
  };

  const handleCloseAddForm = () => {
    setOpenNewMedication(false);
  };

  const manageListContent = (
    <>
      <Button
        variant="contained"
        color="primary"
        fullWidth
        size="small"
        startIcon={<Add />}
        onClick={handleOpenAddForm}
        sx={{
          textTransform: "none",
          fontWeight: 600,
          fontSize: "0.8125rem",
          py: 0.85,
          borderRadius: 2,
          boxShadow: "none",
          "&:hover": { boxShadow: "none" },
        }}
      >
        Adicionar medicamento
      </Button>
      <Box sx={{ mt: 0.75 }}>
        <ControlPrescriptions
          loading={isInitialLoading}
          data={medicationData}
          setRefetch={() => setRefetchMedication()}
          isRefetching={isRefetching}
          prescriptionDocuments={prescriptionDocuments}
          handleOpen={handleOpen}
          setBinaryUrl={setBinaryUrl}
        />
      </Box>
    </>
  );

  const prescriptionsContent = isInitialLoading ? (
    <Stack alignItems="center" justifyContent="center" py={4}>
      <CircularLoading />
    </Stack>
  ) : (
    <MedicalNotes
      sign
      rowData={prescriptionRowData}
      medicationRequest={medicationRequestEntries}
      handleOpen={handleOpen}
      setBinaryUrl={setBinaryUrl}
    />
  );

  const mobileBody = (
    <ControlMedicationsMobileBody
      selectedTab={selectedTab}
      onSelectTab={setSelectedTab}
      manageCount={manageCount}
      prescriptionCount={prescriptionCount}
      manageTabContent={manageListContent}
      prescriptionsContent={prescriptionsContent}
    />
  );

  /**
   * Página web: somente os cards em 3 colunas, preenchendo a largura da tela.
   */
  const webPageBody = (
    <Stack width="100%" spacing={2} sx={{ minWidth: 0 }}>
      {!openNewMedication && (
        <Button
          variant="contained"
          color="primary"
          size="small"
          startIcon={<Add />}
          onClick={handleOpenAddForm}
          sx={{
            alignSelf: "flex-start",
            textTransform: "none",
            fontWeight: 600,
            fontSize: "0.8125rem",
            py: 0.85,
            px: 2,
            borderRadius: 2,
            boxShadow: "none",
            "&:hover": { boxShadow: "none" },
          }}
        >
          Adicionar medicamento
        </Button>
      )}

      {openNewMedication ? (
        <NewMedication
          setModalOpen={setOpenNewMedication}
          setRefetch={() => setRefetchMedication()}
        />
      ) : (
        <ControlPrescriptions
          loading={isInitialLoading}
          data={medicationData}
          setRefetch={() => setRefetchMedication()}
          isRefetching={isRefetching}
          prescriptionDocuments={prescriptionDocuments}
          handleOpen={handleOpen}
          setBinaryUrl={setBinaryUrl}
          columns={3}
        />
      )}
    </Stack>
  );

  const overlays = (
    <>
      <MobileBottomSheet
        open={openNewMedication}
        onClose={handleCloseAddForm}
        title="Adicionar medicamento"
        subtitle="Informe os dados do medicamento"
        confirmLabel="Salvar"
        confirmType="submit"
        confirmForm={NEW_MEDICATION_FORM_ID}
        zIndexOffset={embedded ? 5 : 0}
      >
        <NewMedication
          hideActions
          formId={NEW_MEDICATION_FORM_ID}
          setModalOpen={setOpenNewMedication}
          setRefetch={() => setRefetchMedication()}
        />
      </MobileBottomSheet>

      {iframeOpen ? (
        <IframeDialog
          handleClose={handleClose}
          isOpen={iframeOpen}
          data={binaryUrl}
        />
      ) : null}
    </>
  );

  // Modo embutido: só o conteúdo (ScreenMobile fica no PatientDash).
  if (embedded) {
    return (
      <>
        {mobileBody}
        {overlays}
      </>
    );
  }

  // Mobile standalone (ex.: MoreOptions) — shell próprio.
  if (isMobileLayout) {
    return (
      <>
        <ScreenMobile
          open={!!open}
          onClose={() => setOpen?.(false)}
          title="Meus Medicamentos"
          subtitle="Gerencie seus medicamentos"
        >
          {mobileBody}
        </ScreenMobile>
        {overlays}
      </>
    );
  }

  // Web: página no lugar do dashboard (não Dialog).
  if (!open) {
    return null;
  }

  return (
    <>
      <Box
        sx={{
          width: "100%",
          minWidth: 0,
          bgcolor: "neutral0.main",
          borderRadius: 2,
          boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.06)",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            p: { xs: 1, sm: 1.5 },
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <PageHeader
            title="Meus Medicamentos"
            description="Gerencie seus medicamentos e visualize as receitas"
            icon={MedicalServices}
          />
        </Box>
        <Box sx={{ px: { xs: 1, sm: 2 }, py: 2 }}>{webPageBody}</Box>
      </Box>
      {/* No web o formulário fica inline; bottom sheet só no mobile. */}
      {iframeOpen ? (
        <IframeDialog
          handleClose={handleClose}
          isOpen={iframeOpen}
          data={binaryUrl}
        />
      ) : null}
    </>
  );
}
